import { ReactNode } from "react"

import {
  FeatureAlert,
  useUltimateAlerts,
} from "../../context/ultimateAlertsContext"
import useStrings from "../../localization/useStrings"
import colors from "../../utils/colors"
import images from "../../utils/images"
import BottomSheet from "../BottomSheet/BottomSheet"
import styles from "./ComparePlansSheet.module.scss"
import {
  ComparePlansFeature,
  FeatureType,
  useComparePlansFeatures,
} from "./useComparePlansFeatures"

const ComparePlansSheet = () => {
  const { state, actions } = useUltimateAlerts()

  const features = useComparePlansFeatures()

  return (
    <BottomSheet
      visible={state.showComparePlansSheet}
      containerClassName={styles.sheetContainer}
    >
      <div className={styles.container}>
        <Header />
        <div className={styles.listContainer}>
          <ListHeader />
          {features.map((feature, index) => (
            <ListItem
              feature={feature}
              onInfoClicked={(featureAlert) => {
                actions.setShowFeatureAlert(featureAlert)
              }}
              key={index}
            />
          ))}
          <ListFooter />
        </div>
      </div>
    </BottomSheet>
  )
}
interface ListBaseProps {
  createLeftContent?: () => ReactNode
  createRightSubContent: (type: FeatureType) => ReactNode
  containerClass: string
}

const ListBase = (props: ListBaseProps) => {
  const types: FeatureType[] = ["free", "premium", "ultimate"]

  return (
    <div className={`${styles.listBaseContainer} ${props.containerClass}`}>
      <div className={styles.listBaseLeft}>
        {props.createLeftContent && props.createLeftContent()}
      </div>
      <div className={styles.listBaseRight}>
        {types.map((type, index) => {
          return (
            <div key={index} className={styles.listBaseRightItem}>
              {props.createRightSubContent(type)}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function styleFromFeatureType(featureType: FeatureType) {
  switch (featureType) {
    case "free":
      return {}
    case "premium":
      return {
        headerBackground: colors.featurePremiumHeaderBackground,
        background: colors.featurePremiumBackground,
      }
    case "ultimate":
      return {
        headerBackground: colors.featureUltimateHeaderBackground,
        background: colors.featureUltimateBackground,
      }
  }
}

function imageForFeature(feature: ComparePlansFeature, type: FeatureType) {
  const img = images.comparePlans

  switch (type) {
    case "free":
      if (feature.type == "free") {
        return img.freeAvailable
      } else {
        return img.freeUnavailable
      }
    case "premium":
      if (feature.type == "free" || feature.type == "premium") {
        return img.premiumAvailable
      } else {
        return img.premiumUnavailable
      }
    case "ultimate":
      return img.ultimateAvailable
  }
}

const Header = () => {
  const { actions } = useUltimateAlerts()
  const strings = useStrings().comparePlans

  return (
    <div className={styles.header}>
      <button
        className={styles.backButton}
        onClick={() => {
          actions.setShowComparePlansSheet(false)
        }}
      >
        <div className={images.backButton.class} />
      </button>
      <label className={styles.headerTitle}>{strings.title}</label>
    </div>
  )
}

const ListHeader = () => {
  const { state } = useUltimateAlerts()
  const strings = useStrings().comparePlans

  return (
    <ListBase
      containerClass={styles.listHeader}
      createRightSubContent={(type) => {
        const featureStyle = styleFromFeatureType(type)

        let title = strings.freeTitle

        if (state.showBasicTitle) {
          title = strings.basicTitle
        }

        if (type == "premium") {
          title = strings.premiumTitle
        }
        if (type == "ultimate") {
          title = strings.ultimateTitle
        }

        return (
          <div
            className={styles.listHeader}
            style={{ backgroundColor: featureStyle.headerBackground }}
          >
            <label className={styles.title}>{title}</label>
          </div>
        )
      }}
    />
  )
}

interface ListItemProps {
  onInfoClicked: (alert: FeatureAlert) => void
  feature: ComparePlansFeature
}

const ListItem = (props: ListItemProps) => {
  const { feature } = props

  return (
    <ListBase
      containerClass={styles.listItem}
      createLeftContent={() => {
        return (
          <div className={styles.titleContainer}>
            <label className={styles.title}>{feature.name}</label>
            {feature.alert && (
              <button
                className={styles.disclaimer}
                onClick={() => {
                  props.onInfoClicked(feature.alert!)
                }}
              >
                <div className={images.info.class} />
              </button>
            )}
          </div>
        )
      }}
      createRightSubContent={(type) => {
        const featureStyle = styleFromFeatureType(type)
        const image = imageForFeature(feature, type)

        let text: string | null = null
        if (type == "premium" && feature.premiumText != null) {
          text = feature.premiumText
        }
        if (type == "ultimate" && feature.ultimateText != null) {
          text = feature.ultimateText
        }

        return (
          <div
            className={styles.contentContainer}
            style={{ backgroundColor: featureStyle.background }}
          >
            {text == null && <div className={image.class} />}
            {text && <label className={styles.contentText}>{text}</label>}
          </div>
        )
      }}
    />
  )
}

const ListFooter = () => {
  return (
    <ListBase
      containerClass={styles.listFooter}
      createRightSubContent={(type) => {
        const featureStyle = styleFromFeatureType(type)

        return (
          <div
            className={styles.listFooter}
            style={{ backgroundColor: featureStyle.background }}
          ></div>
        )
      }}
    />
  )
}

export default ComparePlansSheet
